import { useState } from "react";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";

const baseSteps = [
  {
    label: "Next Step",
    title: "Create a Nonprofit",
    description: "Complete your nonprofit profile",
  },
  {
    label: "Step 2",
    title: "Awaiting Status",
    description: "Get your nonprofit approved",
  },
  {
    label: "Step 3",
    title: "Stripe Onboarding",
    description: "Complete your Stripe's onboarding",
  },
  {
    label: "Step 4",
    title: "Create Your First Campaign",
    description: "Create an animal, product, or capital campaign",
  },
  {
    label: "Step 5",
    title: "Upload Donor List",
    description: "Organize all your donors",
  },
  {
    label: "Step 6",
    title: "Email Donor List",
    description: "Send emails to donors of your campaign from the campaign list",
  },
];

function OnboardingChecklist({ setTab, nonprofitCreated = false, isApproved = false, stripeConnected = false, campaignCreated = false, donorListUploaded = false, onCreateCampaign, emailStepCompleted = false, onEmailStepComplete, onViewDashboard }) {
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);

  const completedCount = baseSteps.filter((_, i) => {
    if (!nonprofitCreated) return false;
    if (stripeConnected && campaignCreated && donorListUploaded && emailStepCompleted) return i <= 5;
    if (stripeConnected && campaignCreated && donorListUploaded) return i <= 4;
    if (stripeConnected && campaignCreated) return i <= 3;
    if (stripeConnected) return i <= 2;
    if (isApproved) return i <= 1;
    return i === 0;
  }).length;
  const completedPercent = Math.round((completedCount / baseSteps.length) * 100);

  const getStepState = (index) => {
    if (!nonprofitCreated) {
      return index === 0 ? "next" : "inactive";
    }
    if (stripeConnected && campaignCreated && donorListUploaded && emailStepCompleted) {
      return "completed";
    }
    if (stripeConnected && campaignCreated && donorListUploaded) {
      if (index <= 4) return "completed";
      if (index === 5) return "next";
      return "inactive";
    }
    if (stripeConnected && campaignCreated) {
      if (index <= 3) return "completed";
      if (index === 4) return "next";
      return "inactive";
    }
    if (stripeConnected) {
      if (index <= 2) return "completed";
      if (index === 3) return "next";
      return "inactive";
    }
    if (isApproved) {
      if (index <= 1) return "completed";
      if (index === 2) return "next";
      return "inactive";
    }
    if (index === 0) return "completed";
    if (index === 1) return "next";
    return "inactive";
  };

  const getStepLabel = (index) => {
    if (!nonprofitCreated) return index === 0 ? "Next Step" : baseSteps[index].label;
    if (stripeConnected && campaignCreated && donorListUploaded && emailStepCompleted) {
      return `Step ${index + 1}`;
    }
    if (stripeConnected && campaignCreated && donorListUploaded) {
      if (index <= 4) return `Step ${index + 1}`;
      if (index === 5) return "Next Step";
      return baseSteps[index].label;
    }
    if (stripeConnected && campaignCreated) {
      if (index <= 3) return `Step ${index + 1}`;
      if (index === 4) return "Next Step";
      return baseSteps[index].label;
    }
    if (stripeConnected) {
      if (index <= 2) return `Step ${index + 1}`;
      if (index === 3) return "Next Step";
      return baseSteps[index].label;
    }
    if (isApproved) {
      if (index <= 1) return `Step ${index + 1}`;
      if (index === 2) return "Next Step";
      return baseSteps[index].label;
    }
    if (index === 0) return "Step 1";
    if (index === 1) return "Next Step";
    return baseSteps[index].label;
  };

  const allComplete = stripeConnected && campaignCreated && donorListUploaded && emailStepCompleted;

  if (allComplete) {
    return (
      <div className="flex-1 bg-gray-100 min-h-screen p-10">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-md p-12 flex flex-col items-center justify-center text-center gap-8">
            <h2 className="!text-[26px] !font-semibold !font-poppins text-blue-navy">
              Congratulations on completing your onboarding
            </h2>
            <SecondaryButton onClick={onViewDashboard} className="px-8">
              View Your Dashboard
            </SecondaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-100 min-h-screen p-10">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-blue-navy !text-[30px] !font-semibold !font-poppins !leading-[40px] text-left mb-2">
          Hello, Welcome To Your Dashboard!
        </h1>
        <p className="text-gray-500 text-left mt-5 mb-8 !font-montserrat !font-medium !text-[16px]">
          Complete these simple steps to get your nonprofit up and running.
        </p>

        {/* Pending Status Banner */}
        {nonprofitCreated && !isApproved && !bannerDismissed && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 flex items-start gap-4 relative">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="!font-bold !text-lg text-gray-900 mb-2">Pending Status</p>
              <p className="!text-sm text-gray-700 mb-4">
                We are currently reviewing your nonprofit information, and you will be able to link your bank account with us once you are approved! Watch out for an email notification for your approval status. In the meantime, check out the many resources we have to offer!
              </p>
              <button
                onClick={() => setTab("Resources")}
                className="!text-sm !font-bold text-gray-900 flex items-center gap-1 hover:underline"
              >
                View Resources ↗
              </button>
            </div>
            <button
              onClick={() => setBannerDismissed(true)}
              className="flex-shrink-0 text-gray-500 hover:text-gray-700 ml-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Onboarding Card */}
        <div className="bg-white rounded-2xl shadow-md p-8">
          <div className="flex justify-between items-center mb-3">
            <h2 className="!text-2xl !font-bold text-gray-900">Account Onboarding</h2>
            <span className="text-sm">
              <span className="text-orange font-semibold">{completedPercent}%</span>{" "}
              <span className="text-gray-500">completed</span>
            </span>
          </div>
          <div className="w-full h-[6px] bg-gray-200 rounded-full mb-6">
            <div
              className="h-full bg-orange rounded-full transition-all duration-500"
              style={{ width: `${completedPercent}%` }}
            />
          </div>

          <div className="flex flex-col gap-6">
            {baseSteps.map((step, index) => {
              const state = getStepState(index);
              const label = getStepLabel(index);

              return (
                <div key={index} className="flex items-center gap-3">
                  {/* Step circle */}
                  {state === "completed" ? (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : state === "next" ? (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-white" />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full border-[2px] border-gray-300 bg-gray-200" />
                  )}

                  {/* Step text */}
                  <div className="flex-1 text-left">
                    <p className="!text-blue !font-montserrat !font-bold !text-[16px] mb-0.5">{label}</p>
                    <p className="!text-gray-900 !font-montserrat !font-bold !text-[16px]">{step.title}</p>
                    <p className="!text-gray-500 !font-montserrat !font-normal !text-[14px]">{step.description}</p>
                  </div>

                  {/* Action button */}
                  {state === "next" && !nonprofitCreated && (
                    <SecondaryButton onClick={() => setTab("Create Nonprofit")} className="px-8">
                      Start
                    </SecondaryButton>
                  )}
                  {state === "next" && nonprofitCreated && !isApproved && (
                    <button className="px-6 py-2 rounded-full border-2 border-gray-800 text-gray-800 font-bold text-sm">
                      Pending
                    </button>
                  )}
                  {state === "next" && isApproved && !stripeConnected && (
                    <SecondaryButton onClick={() => setTab("Nonprofit Profile")} className="px-8">
                      Start
                    </SecondaryButton>
                  )}
                  {state === "next" && campaignCreated && index === 4 && (
                    <SecondaryButton onClick={() => setTab("Donor List")} className="px-8">
                      Start
                    </SecondaryButton>
                  )}
                  {state === "next" && donorListUploaded && index === 5 && (
                    <SecondaryButton onClick={onEmailStepComplete} className="px-8">
                      Complete
                    </SecondaryButton>
                  )}
                  {state === "next" && stripeConnected && index === 3 && (
                    <div
                      className="relative"
                      onMouseEnter={() => setCampaignDropdownOpen(true)}
                      onMouseLeave={() => setCampaignDropdownOpen(false)}
                    >
                      <SecondaryButton className="px-8">
                        Start
                      </SecondaryButton>
                      {campaignDropdownOpen && (
                        <div className="absolute right-0 top-full z-10 bg-white border border-gray-200 rounded-xl shadow-lg py-1 min-w-[180px]">
                          <button
                            onClick={() => { setCampaignDropdownOpen(false); onCreateCampaign("Animal"); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Animal Campaign
                          </button>
                          <button
                            onClick={() => { setCampaignDropdownOpen(false); onCreateCampaign("Capital"); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Capital Campaign
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingChecklist;
